import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Button from '../../components/atoms/Button';
import TextField from '../../components/molecules/TextField';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import { DEFAULT_SCHEDULE, SCHEDULE_FREQUENCIES, TRANSLATION_KEY } from '../constants';
import { cronToPreset, getScheduleSummary, presetToCron } from '../utils';
import ScheduleFrequencyOptions from './ScheduleFrequencyOptions';
import ScheduleDaysPicker from './ScheduleDaysPicker';
import css from './SchedulePopover.css';

const POPOVER_KEY = `${TRANSLATION_KEY}.SCHEDULE_POPOVER`;
const SCHEDULE_KEY = `${TRANSLATION_KEY}.SCHEDULE`;

const formatPresetTime = ({ hour, minute = 0 }) =>
    `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

const presetFromCron = cron => (cron && cronToPreset(cron)) || DEFAULT_SCHEDULE;

const SchedulePopover = ({ cron = null, onSave, onClose }) => {
    const [preset, setPreset] = useState(() => presetFromCron(cron));
    const popoverRef = useRef(null);
    const shownCron = useRef(cron);

    // The composer can swap the draft under an open popover, so the editor follows the cron it
    // is given instead of keeping the one it mounted with.
    useEffect(() => {
        if (cron === shownCron.current) return;
        shownCron.current = cron;
        setPreset(presetFromCron(cron));
    }, [cron]);

    // The popover lives inside the composer, so a stray click or Escape must close only the
    // popover - the composer keeps whatever the user has typed.
    useEffect(() => {
        const onPointerDown = event => {
            if (!popoverRef.current?.contains(event.target)) onClose();
        };
        const onKeyDown = event => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            onClose();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [onClose]);

    const selectFrequency = frequency =>
        setPreset(prev => ({
            ...prev,
            frequency,
            days: frequency === SCHEDULE_FREQUENCIES.WEEKLY && !prev.days.length ? DEFAULT_SCHEDULE.days : prev.days,
        }));

    const setTime = time => {
        const [hour, minute] = time.split(':').map(Number);
        if (Number.isInteger(hour) && Number.isInteger(minute)) setPreset(prev => ({ ...prev, hour, minute }));
    };

    const summary = getScheduleSummary(preset);
    const saveDisabled = preset.frequency === SCHEDULE_FREQUENCIES.WEEKLY && !preset.days.length;

    return (
        <div ref={popoverRef} className={css.popover} role="dialog">
            <div className={css.header}>
                <div className={css.title}>
                    <Translate id={`${POPOVER_KEY}.TITLE`} />
                </div>
                <Icon name="x" size={IconSizes.X_SMALL} className={css.close} onClick={onClose} />
            </div>
            <ScheduleFrequencyOptions frequency={preset.frequency} onChange={selectFrequency} />
            {preset.frequency === SCHEDULE_FREQUENCIES.WEEKLY && (
                <ScheduleDaysPicker days={preset.days} onChange={days => setPreset(prev => ({ ...prev, days }))} />
            )}
            {preset.frequency !== SCHEDULE_FREQUENCIES.HOURLY && (
                <>
                    <div className={css.sectionLabel}>
                        <Translate id={`${SCHEDULE_KEY}.TIME`} />
                    </div>
                    <TextField
                        type="time"
                        value={formatPresetTime(preset)}
                        containerClass={css.timeField}
                        onChange={setTime}
                    />
                </>
            )}
            <div className={css.footer}>
                <div className={css.summary}>
                    <Translate id={`${SCHEDULE_KEY}.${summary.key}`} data={summary.data} />
                </div>
                <div className={css.actions}>
                    <Button type="flat" onClick={onClose}>
                        <Translate id={`${POPOVER_KEY}.CANCEL`} />
                    </Button>
                    <Button type="primary" disabled={saveDisabled} onClick={() => onSave(presetToCron(preset))}>
                        <Translate id={`${POPOVER_KEY}.SAVE`} />
                    </Button>
                </div>
            </div>
        </div>
    );
};

SchedulePopover.propTypes = {
    cron: PropTypes.string,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default SchedulePopover;
